import { useEffect } from "react";

const SITE_URL = "https://daosads.com";
const DEFAULT_IMAGE =
  "https://customer-assets.emergentagent.com/job_791cc221-61ed-4c3e-8c20-ef0f4d6cb1c3/artifacts/vxx2qxkz_5.png";

function setMeta(selector, attr, value) {
  if (value == null) return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    const [, attrName, attrValue] = selector.match(/\[(.+?)="(.+?)"\]/) || [];
    if (attrName && attrValue) el.setAttribute(attrName, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

function setLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * Injects multiple JSON-LD blocks. Each call replaces the previously
 * injected per-page blocks (tagged with data-seo="page").
 */
function setJsonLd(schemas = []) {
  // Remove old per-page blocks
  document.head.querySelectorAll('script[type="application/ld+json"][data-seo="page"]').forEach((n) => n.remove());
  (schemas || []).forEach((schema) => {
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.setAttribute("data-seo", "page");
    s.text = JSON.stringify(schema);
    document.head.appendChild(s);
  });
}

/**
 * Per-page SEO. Use this in every public page component.
 */
export function usePageSEO({
  title,
  description,
  path = "",
  image = DEFAULT_IMAGE,
  type = "website",
  jsonLd = [],
}) {
  useEffect(() => {
    if (title) document.title = title;
    if (description) setMeta('meta[name="description"]', "content", description);

    const url = `${SITE_URL}${path || ""}`;
    setLink("canonical", url);

    // Open Graph
    setMeta('meta[property="og:title"]', "content", title);
    setMeta('meta[property="og:description"]', "content", description);
    setMeta('meta[property="og:url"]', "content", url);
    setMeta('meta[property="og:type"]', "content", type);
    setMeta('meta[property="og:image"]', "content", image);

    // Twitter
    setMeta('meta[name="twitter:title"]', "content", title);
    setMeta('meta[name="twitter:description"]', "content", description);
    setMeta('meta[name="twitter:image"]', "content", image);
    setMeta('meta[name="twitter:card"]', "content", "summary_large_image");

    // JSON-LD
    setJsonLd(jsonLd);

    window.scrollTo({ top: 0, behavior: "instant" });
  }, [title, description, path, image, type, JSON.stringify(jsonLd)]);
}

/**
 * Helper for breadcrumbs schema.
 */
export function breadcrumb(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${SITE_URL}${it.path}`,
    })),
  };
}

/**
 * Helper for FAQ schema.
 */
export function faqSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

/**
 * Helper for a Service schema (used on service pages).
 */
export function serviceSchema({ name, description, path, areaServed = "Latinoamérica" }) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name,
    description,
    url: `${SITE_URL}${path}`,
    areaServed,
    provider: {
      "@type": "ProfessionalService",
      "@id": "https://daosads.com/#organization",
      name: "Daos Ads",
      url: SITE_URL,
    },
  };
}
